import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { KeycloakService } from '../services/keycloak.service';

async function tryInitialTokenRefresh(
  authService: AuthService,
  keycloakService: KeycloakService,
): Promise<void> {
  try {
    if (
      isTokenRefreshNeeded(keycloakService) &&
      keycloakService.hasValidRefreshToken()
    ) {
      await authService.updateToken(0);
    }
  } catch (error) {
    console.warn('[AuthGuard] Token refresh attempt failed:', error);
  }
}

async function syncIfExpired(
  authService: AuthService,
  keycloakService: KeycloakService,
): Promise<void> {
  if (!authService.isLoggedIn() && keycloakService.isTokenExpired()) {
    await authService.syncWithKeycloak();
  }
}

async function handleExpiringSession(
  authService: AuthService,
  keycloakService: KeycloakService,
): Promise<boolean> {
  if (
    !isTokenRefreshNeeded(keycloakService) ||
    !keycloakService.hasValidRefreshToken()
  ) {
    return false;
  }
  const newToken = await authService.updateToken(0);
  return !!(newToken && authService.isSessionValid());
}

export const unifiedAuthGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean> => {
  const authService = inject(AuthService);
  const keycloakService = inject(KeycloakService);
  const router = inject(Router);

  if (!authService.isKeycloakReady()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  await tryInitialTokenRefresh(authService, keycloakService);
  await syncIfExpired(authService, keycloakService);

  const isLoggedIn = authService.isLoggedIn();
  const isSessionValid = authService.isSessionValid();
  const isKeycloakAuthenticated =
    authService.isKeycloakReady() && keycloakService.isAuthenticated();

  if (isKeycloakAuthenticated && isLoggedIn && !isSessionValid) {
    try {
      if (await handleExpiringSession(authService, keycloakService)) {
        return true;
      }
    } catch (error) {
      console.error('[AuthGuard] Token refresh failed:', error);
    }
  }

  if (!authService.isLoggedIn() || !authService.isSessionValid()) {
    navigateToPath(router, '/login', state);
    return false;
  }

  return checkRoleBasedAccess(route, state, authService, router);
};

function isTokenRefreshNeeded(keycloakService: KeycloakService): boolean {
  const isExpired = keycloakService.isTokenExpired();
  const tokenParsed = keycloakService.getTokenParsed();
  if (!isExpired && tokenParsed?.exp) {
    const now = Math.floor(Date.now() / 1000);
    return tokenParsed.exp - now < 60;
  }
  return isExpired;
}

function navigateToPath(
  router: Router,
  path: string,
  state: RouterStateSnapshot,
): void {
  try {
    const decodedReturnUrl = decodeURIComponent(state.url);
    router.navigate([path], { queryParams: { returnUrl: decodedReturnUrl } });
  } catch (error) {
    console.warn('Failed to decode return URL:', state.url, error);
    router.navigate([path]);
  }
}

function checkRoleBasedAccess(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authService: AuthService,
  router: Router,
): boolean {
  const requiredRoles = route.data?.['roles'] as string[];
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }
  const currentUser = authService.currentUser();
  if (!currentUser) {
    navigateToPath(router, '/login', state);
    return false;
  }
  const hasRequiredRole = requiredRoles.some((role) =>
    authService.hasRole(role),
  );
  if (!hasRequiredRole) {
    navigateToPath(router, '/unauthorized', state);
    return false;
  }
  return true;
}

/**
 * Simple Auth Guard (alias for backward compatibility)
 * Use this for routes that only need basic authentication
 */
export const simpleAuthGuard = unifiedAuthGuard;

/**
 * Admin Auth Guard
 * Use this for routes that require admin privileges
 */
export const adminAuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  // Create a new route with admin roles
  const routeWithAdminRole = {
    ...route,
    data: {
      ...route.data,
      roles: ['Administrator', 'Admin'],
    },
  } as unknown as ActivatedRouteSnapshot;

  return unifiedAuthGuard(routeWithAdminRole, state);
};

/**
 * Manager Auth Guard
 * Use this for routes that require manager privileges
 */
export const managerAuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  // Create a new route with manager roles
  const routeWithManagerRole = {
    ...route,
    data: {
      ...route.data,
      roles: ['Administrator', 'Admin', 'Manager'],
    },
  } as unknown as ActivatedRouteSnapshot;

  return unifiedAuthGuard(routeWithManagerRole, state);
};
